import { useEffect, useReducer } from 'react';
import {
  getEquippedCosmetics,
  EquippedCosmetics,
} from '../../api/payment/store.api';

/**
 * Cache cosmetic (nametag + hiệu ứng avatar) theo userId cho toàn app.
 * Nhiều chỗ render tên/avatar cùng gọi useCosmetic(userId) → gom id lại,
 * debounce ~60ms rồi gọi 1 lượt POST /store/equipped-cosmetics (batch).
 * Kết quả cache lại (kể cả "không có gì" = null) để không gọi lại.
 */
const cache = new Map<string, EquippedCosmetics | null>();
const pending = new Set<string>();
const subscribers = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

const notify = () => subscribers.forEach((fn) => fn());

const flush = () => {
  timer = null;
  const ids = Array.from(pending);
  pending.clear();
  if (ids.length === 0) return;
  getEquippedCosmetics(ids)
    .then((list) => {
      // mặc định null cho mọi id đã hỏi (tránh hỏi lại), rồi ghi đè id có cosmetic
      ids.forEach((id) => {
        if (!cache.has(id)) cache.set(id, null);
      });
      (list || []).forEach((e) => cache.set(e.userId, e));
      notify();
    })
    .catch(() => {
      // lỗi mạng: đánh dấu null tạm để không spam; lần mount sau vẫn thử lại nếu component mới
      ids.forEach((id) => {
        if (!cache.has(id)) cache.set(id, null);
      });
      notify();
    });
};

const requestCosmetic = (userId?: string) => {
  if (!userId || cache.has(userId) || pending.has(userId)) return;
  pending.add(userId);
  if (!timer) timer = setTimeout(flush, 60);
};

const getCached = (userId?: string): EquippedCosmetics | null =>
  userId ? cache.get(userId) ?? null : null;

const subscribe = (fn: () => void) => {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
};

/** Xoá cache 1 user (gọi sau khi chính mình gắn/tháo cosmetic để cập nhật ngay). */
export const invalidateCosmetic = (userId?: string) => {
  if (!userId) return;
  cache.delete(userId);
  pending.delete(userId); // cho phép hỏi lại dù đang chờ
  // Fetch LẠI ngay (flush sẽ notify với data mới). Nếu chỉ delete+notify thì useCosmetic
  // không re-fetch (useEffect chỉ chạy theo [userId]) → cosmetic biến mất tới khi remount.
  requestCosmetic(userId);
};

/** Trả cosmetic đang gắn của user (null nếu chưa có/không gắn). Tự động fetch batch. */
export const useCosmetic = (userId?: string): EquippedCosmetics | null => {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    requestCosmetic(userId);
    return subscribe(() => force());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);
  return getCached(userId);
};
